import { useQuery } from '@tanstack/react-query';
import { useAppSelector } from '~/hooks';
import * as Service from '../services/GetDiscountCodes';

/**
 * Query keys for the office's discount-code history. Office-scoped: switching
 * DSO child must re-fetch, because codes belong to the child office the request
 * is bound to (`x-office`), not to the signed-in parent.
 */
export const discountCodeKeys = {
  all: ['applied-codes'] as const,
  list: (officeId?: string | null) => [...discountCodeKeys.all, 'list', officeId] as const,
};

interface IUseGetDiscountCodesArgs {
  /** The location Billing Details was opened for; the selected office when omitted. */
  officeId?: string;
  /** Lets a caller that would discard the list (flag off, a parent's overview) skip the request. */
  enabled?: boolean;
}

/**
 * Every discount this office holds — applied codes and codeless discounts, in
 * the order the API returns them — for the Promo Codes list on Payment
 * Information and the dashboard's active-promo tile, which share the cache.
 *
 * @returns discountCodes, isLoading, isError, refetch
 */
export const useGetDiscountCodes = ({
  officeId,
  enabled = true,
}: IUseGetDiscountCodesArgs = {}) => {
  const selectedOfficeId = useAppSelector((state) => state.user.selectedOfficeId);

  const query = useQuery({
    queryKey: discountCodeKeys.list(officeId ?? selectedOfficeId),
    queryFn: () => Service.getDiscountCodes(officeId),
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
  });

  return {
    discountCodes: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
};
